// F-093 Validation des dépenses par seuil.
import { useState } from "react";
import { del, patch, post, put, upload } from "@core/api";
import { dateTime, money, percent } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg, useRestricted } from "@core/org";
import { Button, Card, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, Tabs, Textarea, fieldError } from "@core/ui";
import { Status, docUrl, parsePercentBp, useOwnerOptions, usePropertyOptions, useQuick, useRun } from "./shared";

type Tab = "demandes" | "regles" | "journal";

const EVENT_LABELS: Record<string, string> = {
  creee: "Demande créée",
  accord_demande: "Accord demandé",
  approbateur_absent: "Aucun approbateur joignable",
  relance: "Relance",
  montant_modifie: "Montant modifié",
  nouvel_accord_requis: "Nouvel accord requis",
  devis_depose: "Devis déposé",
  approuvee: "Accord donné",
  refusee: "Accord refusé",
  delai_depasse: "Délai de réponse dépassé",
  engagee: "Dépense engagée",
  engagee_en_urgence: "Engagée en urgence",
  annulee: "Demande annulée",
};

export function ExpensesPage() {
  const [tab, setTab] = useState<Tab>("demandes");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Validations de dépenses"
        intro="Au-delà du seuil fixé pour un mandant, son accord est demandé avant d’engager la dépense, par son portail ou par un lien reçu par e-mail. Le montant et le devis approuvés sont conservés."
      />
      <Notice>L’accord du mandant autorise l’engagement de la dépense ; il ne vaut ni signature du devis ni paiement.</Notice>
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        items={[
          { value: "demandes", label: "Demandes" },
          { value: "regles", label: "Seuils par mandant" },
          { value: "journal", label: "Journal" },
        ]}
      />
      {tab === "demandes" ? <Requests /> : null}
      {tab === "regles" ? <Rules /> : null}
      {tab === "journal" ? <Journal /> : null}
    </div>
  );
}

function Requests() {
  const org = useOrg();
  const [status, setStatus] = useState("");
  const q = useGet<any>(org.api(`/pro/expense-requests${status ? `?status=${status}` : ""}`));
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="stack">
      <Card title="Demandes">
        <Select
          label="Statut"
          value={status}
          options={[
            { value: "", label: "Tous" },
            { value: "en_attente", label: "Accord attendu" },
            { value: "approuvee", label: "Approuvées" },
            { value: "refusee", label: "Refusées" },
            { value: "expiree", label: "Sans réponse" },
            { value: "engagee", label: "Engagées" },
            { value: "non_requise", label: "Sans accord requis" },
          ]}
          onChange={(e) => setStatus(e.target.value)}
        />
        {q.isLoading ? <Loading /> : null}
        {q.data && !q.data.items.length ? <Empty title="Aucune demande">Créez une demande pour chaque devis à engager : Bailivo applique le seuil du mandant.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Demandes d’accord">
            <thead>
              <tr>
                <th>Objet</th>
                <th>Mandant</th>
                <th>Logement</th>
                <th className="num">Montant</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r: any) => (
                <tr key={r.id}>
                  <td>
                    {r.title}
                    {r.supplier ? <span className="block muted small">{r.supplier}</span> : null}
                  </td>
                  <td>{r.owner_name ?? "—"}</td>
                  <td>{r.property_label ?? "—"}</td>
                  <td className="num">{money(r.amount_cents)}</td>
                  <td>
                    <Status kind="expense" value={r.status} />
                    {r.urgent ? <span className="block small">Engagée en urgence</span> : null}
                  </td>
                  <td className="right">
                    <Button variant="discret" small onClick={() => setOpen(r.id)}>
                      Ouvrir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {org.can("operate") ? <CreateRequest /> : null}
      {open ? <RequestDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function CreateRequest() {
  const org = useOrg();
  const invalidate = useInvalidate();
  const { options: properties } = usePropertyOptions(org.orgId, "Aucun logement précis");
  const owners = useOwnerOptions(org.orgId, "Mandant du logement");
  const restricted = useRestricted();
  const contractors = useGet<any>(restricted ? null : org.api("/pro/contractors"));
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ title: "", supplier: "", description: "", property_id: "", owner_id: "", contractor_id: "" });
  const [amount, setAmount] = useState<number | null>(null);
  const [result, setResult] = useState<any>(null);
  const [key, setKey] = useState(0);
  return (
    <Card title="Nouvelle demande">
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const body = { ...form, amount_cents: amount ?? 0, property_id: form.property_id || null, owner_id: form.owner_id || null, contractor_id: form.contractor_id || null };
          const r: any = await run(() => post(org.api("/pro/expense-requests"), body));
          if (r) {
            setResult(r);
            setForm({ ...form, title: "", supplier: "", description: "" });
            setAmount(null);
            setKey(key + 1);
            invalidate(org.api("/pro"));
          }
        }}
      >
        <Input label="Objet" required value={form.title} error={fieldError(error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div className="form-row">
          <Select label="Logement" value={form.property_id} options={properties} onChange={(e) => setForm({ ...form, property_id: e.target.value })} />
          <Select label="Mandant" value={form.owner_id} options={owners} help="Par défaut, le propriétaire du logement." onChange={(e) => setForm({ ...form, owner_id: e.target.value })} />
        </div>
        <div className="form-row">
          <Select
            label="Artisan de l’annuaire"
            value={form.contractor_id}
            options={[{ value: "", label: "Aucun" }, ...(contractors.data?.items ?? []).map((c: any) => ({ value: c.id, label: c.name }))]}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}
          />
          <Input label="Fournisseur" value={form.supplier} help="Sert à repérer plusieurs devis d’un même fournisseur." onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          <MoneyInput key={key} label="Montant TTC du devis" cents={amount} onChange={setAmount} error={fieldError(error, "amount_cents")} required />
        </div>
        <Textarea label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <ErrorBox error={error && !fieldError(error, "title") && !fieldError(error, "amount_cents") ? error : null} />
        {result ? (
          <Notice tone={result.warning ? "attention" : "succes"} title={`Demande créée : ${result.request.approval_reason}`}>
            {result.warning ?? (result.request.status === "en_attente" ? `Demande d’accord envoyée à ${result.request.approver_email}.` : "Aucun accord n’est requis : vous pouvez engager la dépense.")}
          </Notice>
        ) : null}
        <div>
          <Button type="submit" variant="secondaire" busy={busy}>
            Créer la demande
          </Button>
        </div>
      </form>
    </Card>
  );
}

function RequestDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<any>(org.api(`/pro/expense-requests/${id}`));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const r = q.data?.request;
  const refresh = () => invalidate(org.api("/pro"));
  const act = async (fn: () => Promise<any>, msg: string) => {
    const res: any = await run(fn, msg);
    if (res) {
      setNotice(res.warning ?? null);
      refresh();
    }
  };
  return (
    <Modal open onClose={onClose} title={r ? r.title : "Demande"}>
      {q.isLoading ? <Loading /> : null}
      {r ? (
        <div className="stack">
          <Status kind="expense" value={r.status} />
          <KeyValues
            items={[
              ["Montant", money(r.amount_cents)],
              ["Montant approuvé", r.approved_amount_cents !== null ? money(r.approved_amount_cents) : "—"],
              ["Motif de la demande", r.approval_reason || "—"],
              ["Seuil appliqué", r.threshold_cents !== null ? `au-delà de ${money(r.threshold_cents)} (tolérance ${percent(r.tolerance_bp, 0)})` : "Aucune règle"],
              ["Approbateur", r.approver_email ?? "Non joignable"],
              ["Réponse", r.decided_at ? `${r.decided_by_label}, ${dateTime(r.decided_at)}${r.decision_comment ? ` : « ${r.decision_comment} »` : ""}` : "—"],
              ["Lien valable jusqu’au", r.token_expires_at ? dateTime(r.token_expires_at) : "—"],
              ["Devis", r.quote_document_id ? <a href={docUrl(r.quote_document_id)} target="_blank" rel="noreferrer">Ouvrir le devis</a> : "Aucun devis déposé"],
              ["Urgence", r.urgent ? r.urgent_reason : "—"],
            ]}
          />
          {notice ? <Notice tone="attention">{notice}</Notice> : null}
          <ErrorBox error={error} />
          {org.can("operate") && !["engagee", "annulee", "refusee"].includes(r.status) ? (
            <>
              <FileDrop label="Déposer le devis" busy={busy} onFiles={(files) => {
                const form = new FormData();
                form.append("file", files[0]);
                act(() => upload(org.api(`/pro/expense-requests/${id}/quote`), form), "Devis déposé.");
              }} />
              <div className="pro-inline-form">
                <MoneyInput label="Nouveau montant" cents={amount} onChange={setAmount} help="Au-delà de la tolérance, un nouvel accord est demandé." />
                <Button variant="secondaire" small busy={busy} disabled={amount === null} onClick={() => act(() => patch(org.api(`/pro/expense-requests/${id}`), { amount_cents: amount }), "Montant modifié.")}>
                  Modifier le montant
                </Button>
              </div>
              <div className="row">
                {["approuvee", "non_requise"].includes(r.status) ? (
                  <Button busy={busy} onClick={() => act(() => post(org.api(`/pro/expense-requests/${id}/commit`), {}), "Dépense engagée.")}>
                    Engager la dépense
                  </Button>
                ) : null}
                {["en_attente", "expiree"].includes(r.status) ? (
                  <Button variant="secondaire" busy={busy} onClick={() => act(() => post(org.api(`/pro/expense-requests/${id}/remind`), {}), "Demande renvoyée.")}>
                    Relancer le mandant
                  </Button>
                ) : null}
                <Button variant="discret" busy={busy} onClick={() => act(() => post(org.api(`/pro/expense-requests/${id}/cancel`), {}), "Demande annulée.")}>
                  Annuler la demande
                </Button>
              </div>
              {["en_attente", "expiree"].includes(r.status) && org.can("decide") ? (
                <div className="stack">
                  <Textarea label="Motif d’urgence" value={reason} help="Fuite, sécurité des personnes… Le mandant est informé et l’engagement est journalisé." onChange={(e) => setReason(e.target.value)} />
                  <div>
                    <Button variant="danger" busy={busy} disabled={!reason.trim()} onClick={() => act(() => post(org.api(`/pro/expense-requests/${id}/commit`), { urgent_reason: reason }), "Dépense engagée en urgence.")}>
                      Engager en urgence sans accord
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          <h3>Journal</h3>
          <ol className="list-plain">
            {(q.data.events as any[]).map((e) => (
              <li key={e.id}>
                <strong>{EVENT_LABELS[e.kind] ?? e.kind}</strong> · {e.actor_label} · {dateTime(e.created_at)}
                {e.amount_cents !== null ? ` · ${money(e.amount_cents)}` : ""}
                {e.details?.motif ? <span className="block muted small">{e.details.motif}</span> : null}
                {e.details?.commentaire ? <span className="block muted small">« {e.details.commentaire} »</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </Modal>
  );
}

function Rules() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/expense-rules"));
  const owners = useOwnerOptions(org.orgId, "Règle par défaut de l’espace");
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ owner_id: "", tolerance: "5", response_days: "7", split_window_days: "30", approver_email: "" });
  const [threshold, setThreshold] = useState<number | null>(50000);
  return (
    <div className="stack">
      <Card title="Seuils d’accord">
        {q.data && !q.data.items.length ? <Empty title="Aucun seuil">Sans règle, aucune dépense ne demande d’accord.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Règles de validation">
            <thead>
              <tr>
                <th>Mandant</th>
                <th className="num">Seuil</th>
                <th className="num">Tolérance</th>
                <th className="num">Délai de réponse</th>
                <th>Approbateur</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r: any) => (
                <tr key={r.id}>
                  <td>{r.owner_name ?? "Défaut de l’espace"}</td>
                  <td className="num">au-delà de {money(r.threshold_cents)}</td>
                  <td className="num">{percent(r.tolerance_bp, 0)}</td>
                  <td className="num">{r.response_days} j</td>
                  <td>{r.approver_email ?? "Portail ou e-mail du mandant"}</td>
                  <td className="right">
                    {org.can("finance") ? (
                      <Button variant="discret" small onClick={async () => (await quick(() => del(org.api(`/pro/expense-rules/${r.id}`)), "Règle supprimée.")) && invalidate(org.api("/pro"))}>
                        Supprimer
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {org.can("finance") ? (
        <Card title="Définir un seuil">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const tolerance = parsePercentBp(form.tolerance);
              const body = {
                owner_id: form.owner_id || null,
                threshold_cents: threshold ?? 0,
                tolerance_bp: tolerance ?? -1,
                response_days: parseInt(form.response_days, 10),
                split_window_days: parseInt(form.split_window_days, 10),
                approver_email: form.approver_email || null,
              };
              if (await run(() => put(org.api("/pro/expense-rules"), body), "Seuil enregistré.")) invalidate(org.api("/pro"));
            }}
          >
            <div className="form-row">
              <Select label="Mandant" value={form.owner_id} options={owners} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} />
              <MoneyInput label="Accord demandé au-delà de" cents={threshold} onChange={setThreshold} error={fieldError(error, "threshold_cents")} />
              <Input label="Tolérance de hausse (%)" inputMode="decimal" value={form.tolerance} error={fieldError(error, "tolerance_bp")} help="Au-delà, un nouvel accord est demandé." onChange={(e) => setForm({ ...form, tolerance: e.target.value })} />
            </div>
            <div className="form-row">
              <Input label="Délai de réponse (jours)" inputMode="numeric" value={form.response_days} error={fieldError(error, "response_days")} onChange={(e) => setForm({ ...form, response_days: e.target.value })} />
              <Input label="Cumul des devis d’un même fournisseur (jours)" inputMode="numeric" value={form.split_window_days} error={fieldError(error, "split_window_days")} onChange={(e) => setForm({ ...form, split_window_days: e.target.value })} />
              <Input label="Adresse de l’approbateur" type="email" value={form.approver_email} error={fieldError(error, "approver_email")} help="Facultatif : sinon le portail ou l’e-mail du mandant." onChange={(e) => setForm({ ...form, approver_email: e.target.value })} />
            </div>
            <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
            <div>
              <Button type="submit" variant="secondaire" busy={busy}>
                Enregistrer le seuil
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

function Journal() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/expense-events"));
  return (
    <Card title="Journal du circuit d’approbation">
      {q.isLoading ? <Loading /> : null}
      <Table caption="Journal">
        <thead>
          <tr>
            <th>Date</th>
            <th>Demande</th>
            <th>Étape</th>
            <th>Par</th>
            <th className="num">Montant</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).map((e: any) => (
            <tr key={e.id}>
              <td>{dateTime(e.created_at)}</td>
              <td>{e.title}</td>
              <td>{EVENT_LABELS[e.kind] ?? e.kind}</td>
              <td>{e.actor_label}</td>
              <td className="num">{e.amount_cents !== null ? money(e.amount_cents) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
